import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { addDays, setDay, isSameMonth } from 'date-fns';
import { FormattedMessage, FormattedDate } from 'react-intl';
import cx from 'classnames';
import DatePicker from 'react-datepicker';
import { localDate, parseIso8601Date } from 'helpers/time';
import { DisclosureArrow } from 'components/DisclosureArrow/DisclosureArrow';
import { DAYS_OF_WEEK } from 'constants/time-and-date';
import { Selector } from 'components/Selector/Selector';
import css from 'components/Selector/Selector.scss';

class DateFilter extends Component {
  state = {
    selectedMonth: this.props.currentMonth,
  };

  handleOnSelectNextDate = event => {
    const { isNextDaySelectable, selectedDate } = this.props;

    event.stopPropagation();

    if (isNextDaySelectable) {
      this.handleOnSelectDate(addDays(parseIso8601Date(selectedDate), 1));
    }
  };

  handleOnSelectPreviousDate = event => {
    event.stopPropagation();

    const { isToday, selectedDate } = this.props;

    if (!isToday) {
      this.handleOnSelectDate(addDays(parseIso8601Date(selectedDate), -1));
    }
  };

  handleOnSelectDate = date => {
    const { currentMonth, nextMonth, onSelectDate } = this.props;

    const { selectedMonth } = this.state;

    const parsedSelectedMonth = parseIso8601Date(selectedMonth);

    if (!isSameMonth(date, parsedSelectedMonth)) {
      this.setState(prevState => ({
        selectedMonth: prevState.selectedMonth === currentMonth ? nextMonth : currentMonth,
      }));
    }

    onSelectDate(localDate(date));
  };

  handleShowNextMonth = () => {
    this.setState({ selectedMonth: this.props.nextMonth });
  };

  handleShowCurrentMonth = () => {
    this.setState({ selectedMonth: this.props.currentMonth });
  };

  handleOnClear = () => {
    const { timezone, onClear } = this.props;
    this.handleShowCurrentMonth();
    onClear(timezone);
    this.handleCloseSelector();
  };

  handleCloseSelector = () => {
    const { onSetIsEditing } = this.props;

    onSetIsEditing(false);
  };

  handleToggleSelector = () => {
    const { isEditing, onSetIsEditing } = this.props;

    onSetIsEditing(!isEditing);
  };

  renderWeekDay(day) {
    // Getting date on day of week so we can pass a date object to FormattedDate which will handle displaying the weekday
    const dayNum = DAYS_OF_WEEK.indexOf(day);
    const dateOnWeekday = setDay(new Date(), dayNum);
    return <FormattedDate value={dateOnWeekday} weekday="short" />;
  }

  renderMonthSelector() {
    const { currentMonth } = this.props;

    const { selectedMonth } = this.state;

    const isCurrentMonth = selectedMonth === currentMonth;

    return (
      <div className="filter-controls filter-controls--top">
        <span className="filter-controls__main">
          <FormattedDate value={parseIso8601Date(selectedMonth)} month="long" />
        </span>
        <DisclosureArrow
          className={cx('filter-controls__arrow filter-controls__arrow--previous', {
            'filter-controls__arrow--disabled': isCurrentMonth,
          })}
          onClick={!isCurrentMonth ? this.handleShowCurrentMonth : undefined}
        />
        <DisclosureArrow
          className={cx('filter-controls__arrow filter-controls__arrow--next', {
            'filter-controls__arrow--disabled': !isCurrentMonth,
          })}
          onClick={isCurrentMonth ? this.handleShowNextMonth : undefined}
        />
      </div>
    );
  }

  render() {
    const {
      minDate,
      maxDate,
      isMobile,
      isSelected,
      isToday,
      selectedDate,
      isEditing,
      isNextDaySelectable,
    } = this.props;

    const { selectedMonth } = this.state;

    // The DatePicker component depends on the window object being available
    return (
      <Selector
        isMobile={isMobile}
        isSelected={isSelected}
        extraClass={css['selection--date']}
        isOpen={isEditing}
        onToggle={this.handleToggleSelector}>
        {({ Controls, Panel, Selection, isOpen }) => (
          <div className="unified-filters__filter unified-filters__filter--date">
            <Selection>
              <span className={css.selection__selectedDate}>
                <FormattedDate value={parseIso8601Date(selectedDate)} weekday="short" month="short" day="numeric" />
              </span>
              <DisclosureArrow
                data-qa="DateFilter.date-previous"
                className={cx(css.selection__arrow, css['selection__arrow--previous'], 'inline-block', {
                  [css['selection__arrow--active']]: isSelected || isOpen,
                  [css['selection__arrow--disabled']]: isToday,
                })}
                onClick={this.handleOnSelectPreviousDate}
              />
              <DisclosureArrow
                data-qa="DateFilter.date-next"
                className={cx(css.selection__arrow, css['selection__arrow--next'], 'inline-block', {
                  [css['selection__arrow--active']]: isSelected || isOpen,
                  [css['selection__arrow--disabled']]: !isNextDaySelectable,
                })}
                onClick={this.handleOnSelectNextDate}
              />
            </Selection>
            <Panel onClose={this.handleCloseSelector}>
              <div className="unified-filters__content unified-filters__content--datepicker">
                {this.renderMonthSelector()}
                <DatePicker
                  inline
                  minDate={parseIso8601Date(minDate)}
                  maxDate={parseIso8601Date(maxDate)}
                  openToDate={parseIso8601Date(selectedMonth)}
                  selected={parseIso8601Date(selectedDate)}
                  onChange={this.handleOnSelectDate}
                  formatWeekDay={this.renderWeekDay}
                />
                <Controls
                  disableClearControl={!isSelected}
                  clearPrompt={<FormattedMessage id="filters.controls.clear.date" defaultMessage="Today" />}
                  onClear={this.handleOnClear}
                  onDone={this.handleCloseSelector}
                />
              </div>
            </Panel>
          </div>
        )}
      </Selector>
    );
  }
}

DateFilter.propTypes = {
  isMobile: PropTypes.bool,
  isSelected: PropTypes.bool,
  isToday: PropTypes.bool,
  isNextDaySelectable: PropTypes.bool.isRequired,
  currentMonth: PropTypes.string.isRequired,
  nextMonth: PropTypes.string.isRequired,
  minDate: PropTypes.string.isRequired,
  maxDate: PropTypes.string.isRequired,
  onClear: PropTypes.func.isRequired,
  onSelectDate: PropTypes.func.isRequired,
  selectedDate: PropTypes.string.isRequired,
  timezone: PropTypes.string,
  isEditing: PropTypes.bool.isRequired,
  onSetIsEditing: PropTypes.func.isRequired,
};

export { DateFilter };
